import { effectivePermissionSetHas, type UserRole } from '@utazzo/shared';

import type { NavItemConfig } from './nav-config';
import type { WebsitePermission } from '@/features/website-cms/lib';

/**
 * Pure access check for a nav item given the caller's effective permission
 * set from GET /me (ADR-0056). Sidebar, command palette, and route guards
 * must all use this - no role-enum bypasses.
 */
export function canAccessNavItem(
  item: NavItemConfig,
  _role: UserRole | null,
  _can: (action: WebsitePermission) => boolean,
  effectivePermissions: Set<string> | null | undefined,
  email?: string | null,
  assignedRoleCodes?: readonly string[] | null,
): boolean {
  // Menu-only per-person suppression (route guard is separate). Case-insensitive.
  if (item.hideForEmails && email && item.hideForEmails.includes(email.trim().toLowerCase())) {
    return false;
  }
  // Menu-only suppression for holders of a custom role code (e.g. marketing_in),
  // which `hideForRoles` cannot target. Route access is handled separately.
  if (
    item.hideForRoleCodes &&
    assignedRoleCodes &&
    item.hideForRoleCodes.some((code) => assignedRoleCodes.includes(code))
  ) {
    return false;
  }
  // 2026-07-07. AND-semantics: an item is accessible only when EVERY declared
  // gate passes. This lets an item combine `requirePermission` (module-level
  // access - e.g. `ops.leads.write`) with `requireRole` (role-scoped access -
  // e.g. approvers only) so we can say "must have write on leads AND be
  // admin or sales-lead". Items with a single gate keep their previous
  // behaviour (fall through on the unset ones).
  // Menu-only suppression: hide the item for named roles even when they
  // satisfy the permission gate below. The route guard is separate, so this
  // never changes what a role can reach by direct URL - only what it sees.
  if (item.hideForRoles && _role !== null && item.hideForRoles.includes(_role)) {
    return false;
  }
  if (item.requirePermission) {
    if (!effectivePermissions) return false;
    if (!effectivePermissionSetHas(effectivePermissions, item.requirePermission)) return false;
  }
  if (item.requireRole) {
    if (_role === null || !item.requireRole.includes(_role)) return false;
  }
  if (item.permission) {
    if (!_can(item.permission)) return false;
  }
  return true;
}
